import React, { useState, useEffect, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import Glitch from 'glitch-javascript-sdk';
import { Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faMapMarkerAlt,
    faGlobe,
    faGamepad,
    faUsers,
    faChevronDown,
    faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import Navigate from '../../../../util/Navigate';

const GameShowStandalonePage = () => {
    const [show, setShow] = useState({});
    const [games, setGames] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
    const { id } = useParams();

    useEffect(() => {

        // Fetch game show details
        Glitch.api.GameShows.view(id)
            .then(response => {
                setShow(response.data.data);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching show:', error);
                setIsLoading(false);
            });

        // Fetch registered game titles
        Glitch.api.GameShows.listTitles(id)
            .then(response => {
                setGames(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching games:', error);
            });
    }, [id]);

    const openInNewTab = (url) => {
        window.open(url, "_blank");
    };

    const hasAddressContent = (address) => {
        return address && (
            address.address_line_1 || address.address_line_2 || address.locality ||
            address.province || address.postal_code || address.country
        );
    };

    const toggleAdditionalInfo = () => {
        setShowAdditionalInfo(!showAdditionalInfo);
    };

    const defaultImage = "/assets/images/titles/no_image_2.png"; // Placeholder for missing images

    if (isLoading) {
        return (
            <div className="d-flex align-items-center justify-content-center vh-100">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <Fragment>
            {/* Fullscreen Banner */}
            {show.banner_image && (
                <div
                    className="bg-image"
                    style={{
                        backgroundImage: `url(${show.banner_image})`,
                        height: '100vh',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        color: 'white',
                    }}
                >
                    <div
                        className="overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        }}
                    ></div>
                    <div
                        className="d-flex flex-column justify-content-center align-items-center h-100"
                        style={{ position: 'relative', zIndex: 1 }}
                    >
                        {show.logo && (
                            <img
                                src={show.logo}
                                alt={show.name}
                                className="img-fluid mb-3"
                                style={{
                                    width: '200px',
                                    objectFit: 'contain',
                                }}
                            />
                        )}
                        <h1 className="display-2 text-center">{show.name}</h1>
                        {show.tagline && (
                            <p className="lead text-center">{show.tagline}</p>
                        )}
                        {show.website && (
                            <div className="mt-4">
                                <a
                                    href={show.website}
                                    className="btn btn-primary btn-lg"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faGlobe} /> Visit Official Website
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Content Section */}
            <div className="container py-5">
                {/* Description */}
                {show.description && (
                    <div className="mb-5">
                        <h2 className="text-center mb-4">About the Showcase</h2>
                        <div
                            className="lead"
                            dangerouslySetInnerHTML={{ __html: show.description }}
                        ></div>
                    </div>
                )}

                {/* Event Details */}
                <div className="row text-center mb-5">
                    {show.start_date && (
                        <div className="col-md-4 mb-3">
                            <FontAwesomeIcon icon={faCalendarAlt} size="2x" />
                            <h4 className="mt-2">Start Date</h4>
                            <p>{new Date(show.start_date).toLocaleDateString()}</p>
                        </div>
                    )}
                    {show.end_date && (
                        <div className="col-md-4 mb-3">
                            <FontAwesomeIcon icon={faCalendarAlt} size="2x" />
                            <h4 className="mt-2">End Date</h4>
                            <p>{new Date(show.end_date).toLocaleDateString()}</p>
                        </div>
                    )}
                    {show.is_virtual ? (
                        <div className="col-md-4 mb-3">
                            <FontAwesomeIcon icon={faGlobe} size="2x" />
                            <h4 className="mt-2">Virtual Event</h4>
                        </div>
                    ) : (
                        <div className="col-md-4 mb-3">
                            <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" />
                            <h4 className="mt-2">Location</h4>
                            <p>{show.location || 'TBA'}</p>
                        </div>
                    )}
                </div>

                {/* Registration Buttons */}
                <div className="text-center mb-5">
                    {show.allow_game_registration && (
                        <Link
                            to={Navigate.gameshowCommunityRegisterDevelopersStandalonePage(id)}
                            className="btn btn-success btn-lg mx-2"
                        >
                            <FontAwesomeIcon icon={faGamepad} /> Register Your Game
                        </Link>
                    )}
                    {show.allow_user_registration && (
                        <Link
                            to={Navigate.gameshowCommunityRegisterUsersStandalonePage(id)}
                            className="btn btn-info btn-lg mx-2"
                        >
                            <FontAwesomeIcon icon={faUsers} /> Register as Viewer
                        </Link>
                    )}
                </div>

                {/* Additional Information */}
                <div className="mb-5">
                    <div
                        className="d-flex justify-content-between align-items-center mb-3"
                        onClick={toggleAdditionalInfo}
                        style={{ cursor: 'pointer' }}
                    >
                        <h2>More Details</h2>
                        <FontAwesomeIcon
                            icon={showAdditionalInfo ? faChevronUp : faChevronDown}
                            size="2x"
                        />
                    </div>
                    <Collapse in={showAdditionalInfo}>
                        <div>
                            {/* Address */}
                            {hasAddressContent(show.address) && (
                                <div className="mb-4">
                                    <h4>Address</h4>
                                    <p>
                                        {show.address.address_line_1 && (
                                            <>{show.address.address_line_1}<br /></>
                                        )}
                                        {show.address.address_line_2 && (
                                            <>{show.address.address_line_2}<br /></>
                                        )}
                                        {show.address.locality && (
                                            <>{show.address.locality}, </>
                                        )}
                                        {show.address.province && (
                                            <>{show.address.province} </>
                                        )}
                                        {show.address.postal_code && (
                                            <>{show.address.postal_code}<br /></>
                                        )}
                                        {show.address.country && (
                                            <>{show.address.country}</>
                                        )}
                                    </p>
                                </div>
                            )}
                            {/* Timezone */}
                            {show.timezone && (
                                <div className="mb-4">
                                    <h4>Timezone</h4>
                                    <p>{show.timezone}</p>
                                </div>
                            )}
                            {/* Additional Descriptions */}
                            {show.description_game_registration && (
                                <div className="mb-4">
                                    <h4>Game Registration Details</h4>
                                    <div
                                        dangerouslySetInnerHTML={{ __html: show.description_game_registration }}
                                    ></div>
                                </div>
                            )}
                            {show.description_user_registration && (
                                <div className="mb-4">
                                    <h4>User Registration Details</h4>
                                    <div
                                        dangerouslySetInnerHTML={{ __html: show.description_user_registration }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </Collapse>
                </div>

                {/* Registered Games */}
                {games.length > 0 && (
                    <div className="mb-5">
                        <h2 className="text-center mb-4">Featured Games</h2>
                        <div className="row">
                            {games.map((game, index) => (
                                <div className="col-md-4 mb-4" key={index}>
                                    <div className="card h-100 shadow-sm">
                                        <img
                                            src={game?.title?.image_main || defaultImage}
                                            alt={game?.title?.name}
                                            className="card-img-top"
                                            style={{ height: '200px', objectFit: 'cover' }}
                                        />
                                        <div className="card-body">
                                            <h5 className="card-title">{game?.title?.name}</h5>
                                            {game?.title?.short_description && (
                                                <p className="card-text">
                                                    {game?.title?.short_description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="d-flex flex-column align-items-center text-center">
                <img src="/android-icon-48x48.png" alt="Icon" />
                <p className="small">
                    <a href="https://www.glitch.fun">Powered By Glitch</a>
                </p>
            </div>

            {/* Footer (Optional) */}
            <div className="bg-dark text-white text-center py-4">
                <p className="mb-0">
                    &copy; {new Date().getFullYear()} {show.name}. All rights reserved.
                </p>
            </div>
        </Fragment>
    );
};

export default GameShowStandalonePage;
